import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as Checkbox from '@radix-ui/react-checkbox';
import { ChevronDown, Check, X, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type Option = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

function classNames(...xs: (string | false | undefined)[]) {
  return xs.filter(Boolean).join(' ');
}

function useDebouncedValue<T>(value: T, delay = 150) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select options…',
  label,
  maxListHeight = 240,
  emptyState = 'No options found',
}: {
  options: Option[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  label?: string;
  maxListHeight?: number;
  emptyState?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue(query, 120);

  const selectedSet = React.useMemo(() => new Set(value), [value]);

  const filtered = React.useMemo(() => {
    if (!debouncedQuery.trim()) return options;
    const q = debouncedQuery.toLowerCase();
    return options.filter(
      o =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.description?.toLowerCase().includes(q) ?? false)
    );
  }, [options, debouncedQuery]);

  const toggle = (val: string) => {
    const next = new Set(selectedSet);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(Array.from(next));
  };

  const remove = (val: string) => {
    if (!selectedSet.has(val)) return;
    onChange(value.filter(v => v !== val));
  };

  const clearAll = () => onChange([]);

  return (
    <div className="w-full">
      {label && <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>}

      <Popover.Root open={open} onOpenChange={setOpen} modal={false}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={classNames(
              'w-full inline-flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5',
              'bg-white text-left transition-colors',
              'focus:outline-none focus:border-slate-900',
              open ? 'border-slate-900' : 'border-slate-200 hover:border-slate-300'
            )}
            aria-haspopup="listbox"
          >
            <div className="min-h-[24px] flex-1 flex items-center flex-wrap gap-1.5">
              {value.length === 0 ? (
                <span className="text-slate-400 text-sm">{placeholder}</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  <AnimatePresence initial={false}>
                    {value.map(v => {
                      const label = options.find(o => o.value === v)?.label || v;
                      return (
                        <motion.span
                          key={v}
                          layout
                          initial={{ scale: 0.92, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.9, opacity: 0 }}
                          className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
                        >
                          {label}
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              remove(v);
                            }}
                            className="rounded p-0.5 hover:bg-slate-200 transition-colors"
                            aria-label={`Remove ${label}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </motion.span>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {value.length > 0 && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Clear
                </button>
              )}
              <ChevronDown
                className={classNames(
                  'h-4 w-4 text-slate-400 transition-transform',
                  open && 'rotate-180'
                )}
              />
            </div>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            side="bottom"
            sideOffset={4}
            collisionPadding={16}
            className="z-[99999] flex flex-col w-[var(--radix-popover-trigger-width)] rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
            style={{ maxHeight: 'var(--radix-popover-content-available-height, 400px)' }}
            onWheel={e => e.stopPropagation()}
            onTouchMove={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 focus-within:border-slate-400 transition-colors">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent text-sm placeholder:text-slate-400 outline-none border-none focus:outline-none focus:ring-0 focus:border-none"
                style={{ boxShadow: 'none' }}
                autoFocus
              />
            </div>

            <div className="mt-2 overflow-y-auto flex-1" style={{ maxHeight: maxListHeight }}>
              {filtered.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">{emptyState}</div>
              ) : (
                <ul role="listbox" aria-multiselectable className="flex flex-col gap-0.5 pr-1">
                  {filtered.map(opt => {
                    const checked = selectedSet.has(opt.value);
                    return (
                      <li key={opt.value}>
                        <button
                          type="button"
                          onClick={() => !opt.disabled && toggle(opt.value)}
                          className={classNames(
                            'w-full group flex items-center gap-3 rounded-md px-2 py-2.5 text-left transition-colors',
                            checked ? 'bg-slate-100' : 'hover:bg-slate-50',
                            opt.disabled && 'opacity-50 cursor-not-allowed'
                          )}
                          role="option"
                          aria-selected={checked}
                        >
                          <Checkbox.Root
                            checked={checked}
                            disabled={opt.disabled}
                            onCheckedChange={() => toggle(opt.value)}
                            className={classNames(
                              'flex h-4.5 w-4.5 items-center justify-center rounded border transition-colors',
                              checked
                                ? 'bg-slate-900 border-slate-900'
                                : 'bg-white border-slate-300 group-hover:border-slate-400'
                            )}
                            aria-label={opt.label}
                          >
                            <Checkbox.Indicator>
                              <Check className="h-3 w-3 text-white" />
                            </Checkbox.Indicator>
                          </Checkbox.Root>
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate text-sm text-slate-900">{opt.label}</span>
                            {opt.description && (
                              <span className="truncate text-xs text-slate-500">{opt.description}</span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">{value.length} selected</span>
              <Popover.Close asChild>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
                >
                  Done
                </button>
              </Popover.Close>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

export default MultiSelect;
